import { wixHttpPost } from './wix-http';
import { normalizeSendCodeResponse, type SendCodeResult } from './phone-verification-result';

/**
 * Ask for a verification code, from the BROWSER rather than through our own pod.
 *
 * This is the whole point of the module. The Wix edge rate-limits the caller's IP, and asking through
 * our own API route made every web user in the world one caller — so the budget was shared by strangers
 * and exhausted daily, blocking people who had done nothing but type their number. Measured over eight
 * days: 135 people refused outright, all of it on four days. Calling it from the browser puts each user
 * on their own address, which is the fix.
 *
 * Reachable same-origin because this ONE function is mapped under our host (see the
 * `symphony.<domain>/phone-verification` mapping on `com.wixpress.nownia.ai-scheduling-assistant-server`).
 * Deliberately the narrow mapping rather than one that exposes the whole artifact: the other nineteen web
 * functions on it have no business being reachable here, and this way a second front door cannot appear by
 * accident.
 *
 * There is no fallback to a server-side route, and that is deliberate rather than an omission. One
 * existed while the mapping propagated; keeping it would have made a routing regression SILENT — traffic
 * would quietly return to the shared budget and start refusing people again, which is exactly the failure
 * this module was written to end. A send that cannot route now fails loudly instead, and shows up as a
 * 404 on `/phone-verification` in the web app's own access log.
 */
const SEND_URL = '/phone-verification';
const TOO_MANY_REQUESTS = 429;

// The code step's resend can also ask for a voice call, so the sender carries every option the
// endpoint accepts rather than the two the first screen happens to offer.
export type DeliveryOption = 'SMS' | 'WHATSAPP' | 'VOICE_CALL';

export interface SendCodeOutcome {
  result?: SendCodeResult;
  /** True when the send was REFUSED rather than failed — the caller must not offer a retry or another channel. */
  refused: boolean;
}

/** The status off a thrown http-client error, whichever shape it arrived in. */
function httpStatusOf(err: unknown): number | undefined {
  const candidate = err as { response?: { status?: unknown }; status?: unknown } | null;
  const status = candidate?.response?.status ?? candidate?.status;
  if (typeof status === 'number') return status;
  const match = /\b(4\d\d|5\d\d)\b/.exec(err instanceof Error ? err.message : String(err));
  return match ? Number(match[1]) : undefined;
}

/**
 * Logged on every send. The number is NEVER included — only the outcome.
 *
 * The aggregatable record of these outcomes is the BI event in `use-send-code-bi.ts`; this is for
 * reading one browser's console next to one user's report.
 */
const LOG = '[send-code]';

export async function sendVerificationCode(phone: string, deliveryOption: DeliveryOption): Promise<SendCodeOutcome> {
  const body = { verificationOption: { phoneOption: { phone, deliveryOption } } };
  try {
    const raw = await wixHttpPost<unknown>(SEND_URL, body);
    const result = normalizeSendCodeResponse(raw);
    console.info(`${LOG} ${deliveryOption} sent=${result.success} hasVerificationId=${!!result.verificationId}`);
    return { result, refused: false };
  } catch (err) {
    const status = httpStatusOf(err);
    if (status === TOO_MANY_REQUESTS) {
      console.warn(`${LOG} ${deliveryOption} REFUSED (429) — the edge's per-caller budget, not this user`);
      return { refused: true };
    }
    console.warn(`${LOG} ${deliveryOption} failed status=${status ?? 'unknown'}`);
    throw err;
  }
}
